import { z } from 'zod';

import {
  AllergenSchema,
  AllergySeveritySchema,
  DietaryPreferenceSchema,
  IMPLEMENTED_ROLES,
  MEDICAL_ACCESS,
  PASSWORD_ELIGIBLE,
  PlayerStatusSchema,
  ROSTER_ACCESS,
  ROSTER_WRITE,
  type Role,
  type RosterAccess,
} from './enums.js';
import {
  DateOnlySchema,
  JerseyNumberSchema,
  PersonNameSchema,
  PhoneSchema,
  TimestampSchema,
  UuidSchema,
} from './primitives.js';

/**
 * Player, medical, and allergy schemas.
 *
 * The structure here is a security control, not an organizational preference.
 * Medical data, allergy data, and the ordinary player record are three separate
 * schemas over three separate tables, so that a handler returning a player
 * cannot accidentally return a medical note. Rubric B2 and B3 are both S0, and
 * an S0 should be hard to violate rather than merely forbidden.
 */

export const PlayerCoreSchema = z.object({
  id: UuidSchema,
  firstName: PersonNameSchema,
  lastName: PersonNameSchema,
  preferredName: PersonNameSchema.nullable(),
  jerseyNumber: JerseyNumberSchema.nullable().optional(),
  birthdate: DateOnlySchema.nullable(),
  position: z.string().trim().max(24).nullable(),
  secondaryPosition: z.string().trim().max(24).nullable(),
  heightIn: z.int().min(30).max(96).nullable(),
  shoots: z.enum(['left', 'right', 'both']).nullable(),
  grade: z.string().trim().max(16).nullable(),
  school: z.string().trim().max(120).nullable(),
  photoMediaId: UuidSchema.nullable(),
  /** Opt in, defaults false, enforced at the public game view boundary (B9). */
  photoPublicConsent: z.boolean(),
  status: PlayerStatusSchema,
});
export type PlayerCore = z.infer<typeof PlayerCoreSchema>;

export const AllergySchema = z.object({
  id: UuidSchema,
  allergen: AllergenSchema,
  /** Required when allergen is 'other', meaningless otherwise. */
  allergenOther: z.string().trim().max(80).nullable(),
  severity: AllergySeveritySchema,
  reactionNotes: z.string().trim().max(500).nullable(),
  epiRequired: z.boolean(),
  /** Guardians re-confirm each season (BUILD_PLAN 4.7). Null means never confirmed. */
  verifiedAt: TimestampSchema.nullable(),
});
export type Allergy = z.infer<typeof AllergySchema>;

export const AllergyInputSchema = AllergySchema.omit({ id: true, verifiedAt: true }).refine(
  (a) => a.allergen !== 'other' || (a.allergenOther !== null && a.allergenOther.length > 0),
  { error: 'allergen_other_required', path: ['allergenOther'] },
);

export const DietaryPreferenceEntrySchema = z.object({
  id: UuidSchema,
  preference: DietaryPreferenceSchema,
  note: z.string().trim().max(200).nullable(),
});
export type DietaryPreferenceEntry = z.infer<typeof DietaryPreferenceEntrySchema>;

/**
 * General medical detail. The most restricted shape in the product.
 *
 * Only the roles `MEDICAL_ACCESS.medicalNotes` grants: Owner, Head Coach and,
 * since ADR-0108, Assistant Coach. Never in any guardian
 * accessible export, unconditionally (B3). Every read by someone other than the
 * player's own guardian writes to `audit_log` (B7).
 */
export const PlayerMedicalSchema = z.object({
  asthma: z.boolean(),
  epiOnSite: z.boolean(),
  notes: z.string().trim().max(2000).nullable(),
  emergencyContactName: PersonNameSchema.nullable(),
  emergencyContactPhone: PhoneSchema.nullable(),
  physicianName: PersonNameSchema.nullable(),
  physicianPhone: PhoneSchema.nullable(),
  insurance: z.string().trim().max(200).nullable(),
  verifiedAt: TimestampSchema.nullable(),
});
export type PlayerMedical = z.infer<typeof PlayerMedicalSchema>;

/**
 * The allergy card, per BUILD_PLAN 4.7.
 *
 * What the family assigned to bring the team meal sees, and nothing else. This
 * is a purpose-limited projection, and the fields NOT here matter more than the
 * ones that are: no other medical notes, no emergency contacts, no physician,
 * no insurance, no last name.
 *
 * First name plus last initial, because the family bringing dinner needs to
 * know which child, not who the child is.
 */
export const AllergyCardEntrySchema = z.object({
  firstName: PersonNameSchema,
  lastInitial: z.string().length(1),
  allergen: AllergenSchema,
  allergenOther: z.string().trim().max(80).nullable(),
  severity: AllergySeveritySchema,
});
export type AllergyCardEntry = z.infer<typeof AllergyCardEntrySchema>;

/**
 * A food preference on the card. Not medical (BUILD_PLAN 4.7), so a plain
 * line beside the allergies and never escalated: that treatment is for
 * medical risk. ADR-0070 records why the card carries these at all when the
 * plan's field list for it does not name them.
 */
export const DietaryCardEntrySchema = z.object({
  firstName: PersonNameSchema,
  lastInitial: z.string().length(1),
  preference: DietaryPreferenceSchema,
  /** What "other" means, as the family wrote it. Null for the named ones. */
  note: z.string().trim().max(200).nullable(),
});
export type DietaryCardEntry = z.infer<typeof DietaryCardEntrySchema>;

export const AllergyCardSchema = z.object({
  /**
   * The meal assignment's event, once meal rotation (BUILD_PLAN 4.9) exists.
   * Null until then: the card is the roster's, not one game's.
   */
  eventId: UuidSchema.nullable(),
  entries: z.array(AllergyCardEntrySchema),
  dietary: z.array(DietaryCardEntrySchema),
  /** Generated from the allergen list, never freeform from a guardian. */
  safeToBring: z.array(z.string()),
  avoid: z.array(z.string()),
  coachPhone: PhoneSchema.nullable(),
});
export type AllergyCard = z.infer<typeof AllergyCardSchema>;

/**
 * Whether a role may see another family's allergy or medical data.
 *
 * One lookup instead of a conditional every call site re-derives. `isOwnPlayer`
 * short-circuits both, because a guardian sees their own player in full and the
 * MEDICAL_ACCESS table governs OTHER families' data only.
 */
export function canSeeAllergies(role: Role, isOwnPlayer: boolean): boolean {
  return isOwnPlayer || MEDICAL_ACCESS[role].allergies;
}

export function canSeeMedicalNotes(role: Role, isOwnPlayer: boolean): boolean {
  return isOwnPlayer || MEDICAL_ACCESS[role].medicalNotes;
}

/**
 * Whether a role may be acted under at all.
 *
 * `club_admin` is reserved (BUILD_PLAN section 3, D5): the enum value exists so
 * a club rollout needs no permission migration, and "any code path encountering
 * this role throws". The throw belongs at the point of ACTING under it, not at
 * the point of possessing it, so a person who happens to hold a reserved
 * membership somewhere is not locked out of the team where they coach.
 */
export function isActionableRole(role: Role): boolean {
  return (IMPLEMENTED_ROLES as readonly Role[]).includes(role);
}

/**
 * What this role may read of a roster.
 *
 * A thin wrapper over the table, and worth having rather than indexing at each
 * call site: it is the one place to put a breakpoint, and it stops the table
 * being read directly in a way that skips the reserved-role question.
 */
export function rosterAccessFor(role: Role): RosterAccess {
  return ROSTER_ACCESS[role];
}

/**
 * Whether this role may change the roster.
 *
 * A thin wrapper over `ROSTER_WRITE`, and worth having for the same reason
 * `rosterAccessFor` is: it is the one place to put a breakpoint, and it stops
 * the table being indexed at a call site next to `rosterAccessFor` where the
 * two would look interchangeable. They are not. A Team Manager reads the whole
 * roster and may not touch it.
 */
export function canWriteRoster(role: Role): boolean {
  return ROSTER_WRITE[role];
}

/**
 * Whether ANY of a person's memberships lets them hold a password.
 *
 * Takes the whole list, not a role, and that is the point. `users` is not
 * tenant-scoped (one human, one email, one account), so a person can be a
 * guardian on their daughter's team and an assistant coach on their son's.
 * Asking "what is their role" has no answer for that person. Asking "is any of
 * them staff" does.
 *
 * The generous direction is deliberate: holding one staff membership anywhere
 * means a password. The alternative, requiring every membership to be staff,
 * would take the password away from a coach the moment their own child joined a
 * team, which is a sign-in that breaks for a reason nobody could guess.
 *
 * An empty list is FALSE. A person with no memberships is not staff, and
 * defaulting the other way would make an account with nothing attached to it
 * the most privileged state in the system.
 */
export function canHoldPassword(roles: readonly Role[]): boolean {
  return roles.some((role) => PASSWORD_ELIGIBLE[role]);
}

/**
 * Whether an access needs an audit row.
 *
 * Reading your own child's allergy list is not an event worth logging and
 * logging it would bury the reads that matter. Reading someone else's is
 * exactly what B7 requires be recorded.
 */
export function requiresMedicalAudit(isOwnPlayer: boolean): boolean {
  return !isOwnPlayer;
}

/**
 * A new player, as a coach types one.
 *
 * `firstName` and `lastName` are required. Jersey numbers are optional until
 * assigned. Everything else is optional and defaulted by the database.
 *
 * `photoPublicConsent` and `photoMediaId` are deliberately NOT here. Consent is
 * an explicit guardian action (rubric B9, and `players.photo_public_consent`
 * defaults false), so it must not be settable on the form a coach uses to add a
 * child. A photo arrives through the media path with its signed URL, not as an
 * id typed into this shape.
 *
 * `status` is absent for a smaller reason: a player being added is active, and
 * offering `injured` at creation invites recording a medical fact in the one
 * screen that has no audit obligation.
 */
export const PlayerCreateSchema = PlayerCoreSchema.omit({
  id: true,
  photoMediaId: true,
  photoPublicConsent: true,
  status: true,
}).partial({
  preferredName: true,
  birthdate: true,
  position: true,
  secondaryPosition: true,
  heightIn: true,
  shoots: true,
  grade: true,
  school: true,
});
export type PlayerCreate = z.infer<typeof PlayerCreateSchema>;

/**
 * An edit. Every field optional, `status` allowed, the photo fields still not.
 *
 * `status` is editable here and not at creation because marking somebody
 * injured is a real thing a coach does mid-season, and this path is the one
 * that writes an audit row.
 */
export const PlayerUpdateSchema = PlayerCreateSchema.partial()
  .extend({ status: PlayerStatusSchema.optional() })
  .refine((value) => Object.keys(value).length > 0, { error: 'no_fields' });
export type PlayerUpdate = z.infer<typeof PlayerUpdateSchema>;
